import {
  CreateEntitlementGroupInterface,
  EntitlementGroupInterface,
  UpdateEntitlementGroupInterface
} from '@thro-types/access-handler';
import {
  Article,
  Booking,
  BookingState,
  CreateOrPatchVacation,
  CreateVacation,
  Order,
  TermsOfAcceptance,
  Product,
  Unavailability,
  UpdateBooking,
  Vacation,
  LenderAvailability,
  UpdateLenderAvailability,
  ShoppingCartItem,
  CreateProduct,
  UpdateProduct,
  Category,
  Location,
  CreateLocation,
  UpdateLocation,
  CreateCategory,
  UpdateCategory,
  Option,
  UpdateOption,
  CreateOption,
  CreateOrder,
  CreateArticle,
  UpdateArticle,
  ArticleList,
  CreateShoppingCartItem,
  UpdateShoppingCartItem
} from '@thro-types/leihbar';

import { AxiosError, AxiosRequestConfig } from 'axios';
import { Gateway } from 'index';

import { ListResponse } from 'types/AppTypes';

export const getVacationsForLenderGroupId = async (
  lenderGroupId: number,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.get<{ vacations: Vacation[]; vacationCount: number }>(
      `/leihbar/v1/vacations/lenderGroupId/${lenderGroupId}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getVacation = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Vacation>(`/leihbar/v1/vacations/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createVacation = async (data: CreateVacation, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Vacation>('/leihbar/v1/vacations', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateVacation = async (
  id: number,
  data: CreateOrPatchVacation,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Vacation>(`/leihbar/v1/vacations/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteVacation = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<Vacation>(`/leihbar/v1/vacations/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getConflictingBookingsInVacation = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<{ bookings: Booking[]; count: number }>(
      `/leihbar/v1/bookings/checkConflictsWithVacation`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getVisibleArticlesForProduct = async (
  productId: number,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.get<[Article[], number]>(
      `/leihbar/v1/products/${productId}/articles/visibleArticlesForProduct`,
      config
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAvailableArticles = async (productId: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Article[]>(
      `/leihbar/v1/products/${productId}/articles/availableArticlesForBooking`,
      config
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getArticlesWithConflictBookings = async (
  productId: number,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.get<Article[]>(
      `/leihbar/v1/products/${productId}/articles/articlesWithConflictBookings`,
      config
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getShoppingCartAvailability = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Record<string, boolean>>(
      `/leihbar/v1/shoppingCart/availability`,
      config
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getArticlesForProduct = async (productId: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ArticleList>(
      `/leihbar/v1/products/${productId}/articles`,
      config
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createLeihbarArticle = async (data: CreateArticle, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Article>(
      `/leihbar/v1/products/${data.productId}/articles`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateLeihbarArticle = async (
  id: number,
  data: UpdateArticle,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Article>(
      `/leihbar/v1/products/${data.productId}/articles/${id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteLeihbarArticle = async (
  { id, productId }: { productId: number; id: number },
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.delete<Article>(
      `/leihbar/v1/products/${productId}/articles/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getConflictionBookingsWithOtherBookings = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<{ bookings: Booking[]; count: number }>(
      `/leihbar/v1/bookings/checkConflictWithBookings`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getUnavailabilitesForProduct = async (
  productId: number,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.get<{
      unavailabilities: Unavailability[];
      articleCount: number;
    }>(`/leihbar/v1/products/${productId}/unavailabilities`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getOrders = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Order>>('/leihbar/v1/orders', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getOrder = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Order>(`/leihbar/v1/orders/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const patchBooking = async (
  id: number,
  data: UpdateBooking,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Booking>(`/leihbar/v1/bookings/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};
export const getProducts = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Product>>('/leihbar/v1/products', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getProduct = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Product>(`/leihbar/v1/products/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getBookings = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Booking>>('/leihbar/v1/bookings', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const setBookingsState = async (
  data: { state: BookingState; orderId: number; bookingIds: number[] },
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Booking[]>(
      `/leihbar/v1/orders/${data.orderId}/bookings/state`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getTermsOfAcceptance = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<TermsOfAcceptance>(`/leihbar/v1/termsOfAcceptance`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateOrCreateTermsOfAcceptance = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.patch<TermsOfAcceptance>(
      `/leihbar/v1/termsOfAcceptance`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const cancelBooking = async (data: { id: number }, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.patch<Booking>('/leihbar/v1/bookings/cancel', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createOrder = async (data: CreateOrder, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Order>(`/leihbar/v1/orders`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const cancelOrder = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Booking[]>(
      `/leihbar/v1/orders/${id}/cancel`,
      undefined,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getLenderAvailability = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<LenderAvailability>(
      `/leihbar/v1/lenderAvailability/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateOrCreateLenderAvailability = async (
  id: number,
  data: UpdateLenderAvailability,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<LenderAvailability>(
      `/leihbar/v1/lenderAvailability/${id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getShoppingCart = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ShoppingCartItem[]>('/leihbar/v1/shoppingCart', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createShoppingCartItem = async (
  data: CreateShoppingCartItem,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<ShoppingCartItem>(
      '/leihbar/v1/shoppingCart/item',
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateShoppingCartItem = async (
  id: number,
  data: UpdateShoppingCartItem,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<ShoppingCartItem>(
      `/leihbar/v1/shoppingCart/item/${id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteShoppingCartItem = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<ShoppingCartItem>(
      `/leihbar/v1/shoppingCart/item/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const cloneProduct = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Product>(
      `/leihbar/v1/products/clone/${id}`,
      undefined,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createProduct = async (data: CreateProduct, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Product>(`/leihbar/v1/products`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateProduct = async (
  id: number,
  data: UpdateProduct,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Product>(`/leihbar/v1/products/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteProduct = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<Product>(`/leihbar/v1/products/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getLeihbarCategories = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Category[]>('/leihbar/v1/categories', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createLeihbarCategory = async (data: CreateCategory, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Category>(`/leihbar/v1/categories`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateLeihbarCategory = async (
  id: number,
  data: UpdateCategory,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Category>(`/leihbar/v1/categories/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteLeihbarCategory = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<Category>(`/leihbar/v1/categories/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getLocations = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Location[]>('/leihbar/v1/locations', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createLocation = async (data: CreateLocation, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Location>(`/leihbar/v1/locations`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateLocation = async (
  id: number,
  data: UpdateLocation,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Location>(`/leihbar/v1/locations/${id}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteLocation = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<Location>(`/leihbar/v1/locations/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getOptionsForProduct = async (productId: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Option[]>(
      `/leihbar/v1/products/${productId}/options`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createOption = async (data: CreateOption, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Option>(
      `/leihbar/v1/products/${data.productId}/options`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateOption = async (id: number, data: UpdateOption, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.patch<Option>(
      `/leihbar/v1/products/${data.productId}/options/${id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteOption = async (
  { id, productId }: { id: number; productId: number },
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.delete<Option>(
      `/leihbar/v1/products/${productId}/options/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createLeihbarAccess = async (
  data: CreateEntitlementGroupInterface,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<EntitlementGroupInterface>(
      `/leihbar/v1/access`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateLeihbarAccess = async (
  id: number,
  data: UpdateEntitlementGroupInterface,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<EntitlementGroupInterface>(
      `/leihbar/v1/access/${id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteLeihbarAccess = async (id: number, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<EntitlementGroupInterface>(
      `/leihbar/v1/access/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};
